/* Global: accessibility repairs for third-party markup and WebMCP form annotations.
   Runs once after DOM ready and again when sliders/drawers inject markup. */
(() => {
  if (window.__ntGlobal) return;
  const SHOP = (document.querySelector('meta[property="og:site_name"]') || {}).content || 'this store';
  window.__ntGlobal = true;

  /* 1. Slider slides: <li> may not carry role=group/tabpanel. Keep them plain list items. */
  const fixSlides = (root) => {
    root.querySelectorAll('li.splide__slide[role]').forEach((li) => {
      li.removeAttribute('role');
      li.removeAttribute('aria-roledescription');
    });
  };

  /* 2. Headings must not carry role=region (it hides the heading role). */
  const fixHeadingRoles = (root) => {
    root.querySelectorAll('h1[role="region"],h2[role="region"],h3[role="region"],h4[role="region"]').forEach((h) => {
      h.removeAttribute('role');
      h.removeAttribute('aria-label');
    });
  };

  /* 3. Icon-only buttons need a name. */
  const fixButtons = (root) => {
    root.querySelectorAll('button.internal-video__play:not([aria-label])').forEach((b) => b.setAttribute('aria-label', 'Play video'));
    root.querySelectorAll('button:not([aria-label]):not([aria-labelledby])').forEach((b) => {
      if (!b.textContent.trim() && !b.querySelector('img[alt]:not([alt=""]), [aria-label]')) {
        const t = b.getAttribute('title') || b.dataset.label;
        if (t) b.setAttribute('aria-label', t);
      }
    });
  };

  /* 4. aria-controls must point at an element that exists. */
  const fixControls = (root) => {
    root.querySelectorAll('[aria-controls]').forEach((el) => {
      const ids = el.getAttribute('aria-controls').split(/\s+/).filter((id) => document.getElementById(id));
      if (ids.length) el.setAttribute('aria-controls', ids.join(' ')); else el.removeAttribute('aria-controls');
    });
  };

  /* 5. Duplicate ids (payment icons are printed in several places with the same <title id>). */
  const fixDuplicateIds = () => {
    const seen = new Map();
    document.querySelectorAll('svg title[id], svg [id^="pi-"]').forEach((el) => {
      const id = el.id;
      const n = (seen.get(id) || 0) + 1;
      seen.set(id, n);
      if (n > 1) {
        const nid = `${id}-${n}`;
        const svg = el.closest('svg');
        if (svg && svg.getAttribute('aria-labelledby') === id) svg.setAttribute('aria-labelledby', nid);
        el.id = nid;
      }
    });
  };

  /* 6. Heading levels must not skip (h2 -> h4). Expose the correct level to assistive tech. */
  const fixHeadingOrder = () => {
    let prev = 0;
    document.querySelectorAll('h1,h2,h3,h4,h5,h6').forEach((h) => {
      if (h.closest('[hidden], template') || !h.getClientRects().length) return;
      const lvl = +(h.getAttribute('aria-level') || h.tagName[1]);
      if (prev && lvl > prev + 1) { h.setAttribute('aria-level', String(prev + 1)); prev = prev + 1; }
      else prev = lvl;
    });
  };

  /* 7. Frames need a title (e.g. third-party widgets). */
  const fixFrames = (root) => {
    root.querySelectorAll('iframe:not([title])').forEach((f) => {
      let host = '';
      try { host = new URL(f.src, location.href).hostname.replace(/^www\./, ''); } catch (e) {}
      f.setAttribute('title', host ? `Embedded content from ${host}` : 'Embedded content');
    });
  };

  /* 8. WebMCP declarative annotations so AI agents can use the store's forms reliably.
        https://developer.chrome.com/docs/ai/webmcp/declarative-api */
  const productName = (form) => {
    const scope = form.closest('product-info, .product, .card-wrapper, .card, quick-add-modal, [data-product-title]') || document;
    const t = scope.querySelector('[data-product-title], .product__title h1, .product__title, .card__heading, h1');
    return (t && t.textContent.trim().replace(/\s+/g, ' ')) || '';
  };
  const describe = (form) => {
    const action = (form.getAttribute('action') || '').split('?')[0].replace(/^https?:\/\/[^/]+/, '');
    if (/\/search$/.test(action)) return ['storefront_search', `Search the ${SHOP} store for products, collections, pages and articles by keyword.`];
    if (/\/cart\/add$/.test(action)) {
      const n = productName(form);
      return ['add_product_to_cart', n ? `Add "${n}" to the shopping basket with the selected finish/variant and quantity.` : 'Add this product to the shopping basket with the selected variant and quantity.'];
    }
    if (/\/cart$/.test(action)) return ['update_basket', 'Review the shopping basket: change item quantities, add an order note, or continue to checkout.'];
    if (/\/contact/.test(action)) {
      if (form.querySelector('[name="contact[email]"]') && !form.querySelector('textarea')) return ['newsletter_signup', `Subscribe an email address to the ${SHOP} newsletter for offers and new products.`];
      return ['contact_store', `Send a message to the ${SHOP} customer service team.`];
    }
    if (/\/account\/login/.test(action)) return ['customer_login', `Sign in to a ${SHOP} customer account.`];
    if (/\/account\/recover/.test(action)) return ['recover_password', `Request a password reset email for a ${SHOP} customer account.`];
    if (/\/account$/.test(action)) return ['create_account', `Create a new ${SHOP} customer account.`];
    if (/\/localization/.test(action)) return ['change_country_language', 'Change the shopping country, currency or language.'];
    if (/\/cart\/(update|change)/.test(action)) return ['update_basket', 'Update quantities of items in the shopping basket.'];
    const label = form.getAttribute('aria-label') || form.querySelector('legend, h2, h3')?.textContent || 'form';
    return ['page_form', `Submit the "${label.trim().slice(0, 60)}" form on this page.`];
  };
  const PARAMS = {
    q: 'Search keywords, e.g. "vanity unit 60cm" or "washing machine cabinet".',
    id: 'The variant id to add. Change it by choosing a finish/size on the page.',
    quantity: 'How many units to add (whole number, at least 1).',
    'contact[email]': 'The email address to subscribe or reply to.',
    'contact[name]': 'Your full name.',
    'contact[body]': 'Your message.',
    note: 'An optional note for the order.'
  };
  const annotateForms = (root) => {
    const used = new Map();
    root.querySelectorAll('form').forEach((form) => {
      if (!form.hasAttribute('toolname')) {
        let [name, desc] = describe(form);
        const n = (used.get(name) || 0) + 1; used.set(name, n);
        if (n > 1 && name !== 'add_product_to_cart') name = `${name}_${n}`;
        form.setAttribute('toolname', name);
        form.setAttribute('tooldescription', desc);
      }
      form.querySelectorAll('input[name]:not([type="hidden"]):not([toolparamdescription]), select[name]:not([toolparamdescription]), textarea[name]:not([toolparamdescription])').forEach((el) => {
        const d = PARAMS[el.name];
        if (d) el.setAttribute('toolparamdescription', d);
      });
    });
  };

  const run = (root = document) => {
    fixSlides(root); fixHeadingRoles(root); fixButtons(root); fixFrames(root); annotateForms(root);
  };
  const runDocumentWide = () => { fixControls(document); fixDuplicateIds(); fixHeadingOrder(); };

  const start = () => {
    run(); runDocumentWide();
    let queued = false;
    new MutationObserver((muts) => {
      if (queued) return;
      if (!muts.some((m) => m.addedNodes.length || (m.type === 'attributes'))) return;
      queued = true;
      requestAnimationFrame(() => { queued = false; run(); runDocumentWide(); });
    }).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['role'] });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
})();
